import React, { useMemo } from 'react';
import { getVehicleBadge, formatCurrency } from '../../utils/helpers';
import PriceCalculationService from '../../services/priceCalculator';

const VehicleCard = ({ vehicle, selected, onSelect, searchData, routeInfo, compact = false }) => {
  const badge = getVehicleBadge(vehicle.className);

  // Dynamic Pricing Calculation
  const priceResult = useMemo(() => {
    if (!vehicle || !searchData) return null;

    const toNum = (v) =>
      typeof v === 'number'
        ? v
        : parseFloat(String(v ?? '').replace(/[^\d.]/g, '')) || 0;

    const distanceKm = (routeInfo?.distanceKm ?? toNum(routeInfo?.distance))
      || (routeInfo?.distanceValue ? routeInfo.distanceValue / 1000 : 0)
      || toNum(searchData?.distance)
      || 0;
    
    const isHourly = searchData?.subService === 'hourly'
      || String(searchData?.type || '').toLowerCase().includes('hourly');

    const pickupAtDate = searchData?.pickupDate && searchData?.pickupTime
      ? `${searchData.pickupDate}T${searchData.pickupTime}`
      : new Date().toISOString();

    if (isHourly) {
      const hours = Number(searchData?.hours || vehicle.pricing?.hours || 2);
      return PriceCalculationService.calculateHourlyPrice(vehicle, hours, distanceKm, pickupAtDate);
    } else {
      const returnMultiplier = searchData?.showReturn ? 2 : 1;
      const waitingMins = Number(searchData?.waitingMins) || 0;
      return PriceCalculationService.calculateTransferPrice(vehicle, distanceKm, pickupAtDate, {}, returnMultiplier, waitingMins);
    }
  }, [vehicle, searchData, routeInfo]);

  const displayTotal = priceResult?.total ?? vehicle.pricing?.totalAmount ?? 0;
  const currency = priceResult?.currency ?? vehicle.pricing?.currency ?? 'USD';
  const isHourly = vehicle.pricing?.isHourly;

  const containerClasses = `w-full glass-panel rounded-2xl p-3 md:p-4 cursor-pointer transition-all relative group border-white/10 ${
    selected 
      ? 'border-blue-500/50 bg-blue-500/5 ring-1 ring-blue-500/30' 
      : 'hover:border-white/20 hover:bg-white/5 shadow-xl'
  } ${compact ? 'py-3' : ''}`;

  const vehicleImage = PriceCalculationService.getVehicleImage(vehicle);

  return (
    <article 
      className={containerClasses} 
      onClick={onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={0}
    >
      <div className="flex items-center gap-4">
        {/* Vehicle Thumbnail */}
        <div className={`w-14 h-14 md:w-20 md:h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/5 border border-white/10 ${compact ? 'w-12 h-12' : ''}`}>
          {vehicleImage ? (
            <img
              src={vehicleImage}
              alt={vehicle.className}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<span class="text-xl">🚗</span>'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xl">🚗</div>
          )}
        </div>

        {/* Vehicle Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col">
            <h3 className="font-black text-sm md:text-base text-white tracking-tight truncate uppercase italic">{vehicle.className}</h3>
            <div className={`flex items-center gap-3 mt-1 ${compact ? 'hidden md:flex' : 'flex'}`}>
              <span className="flex items-center gap-1.5 text-[10px] md:text-xs text-gray-500 font-bold uppercase tracking-widest">
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                </svg>
                {vehicle.paxMax}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] md:text-xs text-gray-500 font-bold uppercase tracking-widest">
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12V8H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4" /><path d="M12 20h.01" />
                </svg>
                {vehicle.bagMax}
              </span>
              {isHourly && (
                <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-500/10 px-2 py-0.5 rounded-md">Hourly</span>
              )}
            </div>
          </div>
        </div>

        {/* Badge - Laptop only */}
        {badge && !compact && (
          <span className={`hidden lg:inline-flex px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-[0.2em] border ${
            badge.text.includes('BEST') ? 'bg-green-500/10 text-green-500 border-green-500/20' : 
            badge.text.includes('POPULAR') ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
            'bg-white/5 text-gray-400 border-white/10'
          }`}>
            {badge.text}
          </span>
        )}

        {/* Pricing Area */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <p className="text-lg md:text-2xl font-black text-white tracking-tighter leading-none mb-1 font-mono">
              {formatCurrency(displayTotal, currency)}
            </p>
            {selected && (
              <p className="text-[8px] font-black text-blue-500 uppercase tracking-widest text-right">Selected Grade</p>
            )}
          </div>

          <button 
            type="button" 
            className={`
              w-10 h-10 md:w-auto md:h-10 px-0 md:px-6 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl
              ${selected 
                ? 'bg-blue-600 text-white shadow-blue-500/30' 
                : 'bg-white/5 border border-white/10 text-gray-500 hover:bg-white/10 hover:text-white'
              }
            `}
          >
            {selected ? (
              <>
                <svg className="w-4 h-4 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <span className="hidden md:inline font-black text-[10px] uppercase tracking-widest">Selected</span>
              </>
            ) : (
              <span className="font-black text-[10px] uppercase tracking-widest">Select</span>
            )}
          </button>
        </div>
      </div>
    </article>
  );
};

export default VehicleCard;